import { io } from 'socket.io-client';
import { reactive } from 'vue';

class AISocketClient {
    constructor() {
        this.state = reactive({
            socket: null,
            generatedText: {},
            isGenerating: {},
            error: null,
            currentChatId: null,
            genStatus: null,
            genvidQueues: {},
            genvidHistory: []
        });
        this.baseDomain = import.meta.env.VITE_AI_API_URL;
    }

    connect() {
        if (!this.state.socket) {
            this.state.socket = io(this.baseDomain, {
                withCredentials: true,
                transports: ['websocket']
            });

            this.state.socket.on('connect', () => {
                console.log('Connected to socket server');
            });

            this.state.socket.on('disconnect', () => {
                console.log('Disconnected from socket server');
            });

            this.state.socket.on('text_chunk', (chunk) => {
                if (chunk.chatId && !chunk.done) {
                    if (!this.state.generatedText[chunk.chatId]) {
                        this.state.generatedText[chunk.chatId] = '';
                    }
                    this.state.generatedText[chunk.chatId] += chunk.text;
                } else if (chunk.done) {
                    this.state.isGenerating[chunk.chatId] = false;
                }
            });

            this.state.socket.on('queue_update', (data) => {
                this.state.genStatus = data;
            });

            this.state.socket.on('error', (err) => {
                console.error('Socket error:', err);
                this.state.error = err;
            });

            // Genvid socket event listeners
            this.state.socket.on('genvid_joined', (data) => {
                console.log('Joined genvid room:', data);
            });

            this.state.socket.on('genvid_status_update', (data) => {
                console.log('Genvid status update:', data);
                this.state.genvidQueues[data.queueId] = data;
            });

            this.state.socket.on('genvid_completed', (data) => {
                console.log('Genvid completed:', data);
                this.state.genvidQueues[data.queueId] = data;
            });

            this.state.socket.on('genvid_error_notification', (data) => {
                console.error('Genvid error:', data);
                this.state.genvidQueues[data.queueId] = data;
            });

            this.state.socket.on('genvid_history', (data) => {
                console.log('Genvid history:', data);
                this.state.genvidHistory = data.videos || [];
            });

            this.state.socket.on('genvid_status', (data) => {
                console.log('Genvid queue status:', data);
                this.state.genvidQueues[data.queueId] = data;
            });

            this.state.socket.on('genvid_queue_subscribed', (data) => {
                console.log('Subscribed to genvid queue:', data);
            });

            this.state.socket.on('genvid_error', (error) => {
                console.error('Genvid socket error:', error);
                this.state.error = error.message;
            });
        }
    }

    async generateText(prompt, options = {}) {
        if (!this.state.socket) {
            this.state.error = 'Socket not connected';
            throw new Error('Socket not connected');
        }

        const chatId = options.chatId || Date.now().toString();
        this.state.currentChatId = chatId;
        this.state.isGenerating[chatId] = true;
        this.state.generatedText[chatId] = '';
        this.state.error = null;

        console.log("Sending prompt:", prompt);
        this.state.socket.emit('generate_text', {
            chatId,
            prompt,
            maxTokens: options.maxTokens || 512,
            temperature: options.temperature || 0.5
        });

        return chatId;
    }

    joinGeneratorRoom(userId) {
        if (!this.state.socket) {
            this.state.error = 'Socket not connected';
            throw new Error('Socket not connected');
        }

        this.state.socket.emit('join_generator', userId);
    }

    leaveGeneratorRoom(userId) {
        if (!this.state.socket) {
            this.state.error = 'Socket not connected';
            throw new Error('Socket not connected');
        }

        this.state.socket.emit('leave_generator', userId);
    }

    joinGenvidRoom(userId, token) {
        if (!this.state.socket) {
            this.state.error = 'Socket not connected';
            throw new Error('Socket not connected');
        }

        this.state.socket.emit('join_genvid', { userId, token });
    }

    leaveGenvidRoom() {
        if (!this.state.socket) {
            this.state.error = 'Socket not connected';
            throw new Error('Socket not connected');
        }

        this.state.socket.emit('leave_genvid');
    }

    subscribeGenvidQueue(queueId) {
        if (!this.state.socket) {
            this.state.error = 'Socket not connected';
            throw new Error('Socket not connected');
        }

        this.state.socket.emit('subscribe_genvid_queue', { queueId });
    }

    getGenvidHistory(options = {}) {
        if (!this.state.socket) {
            this.state.error = 'Socket not connected';
            throw new Error('Socket not connected');
        }

        this.state.socket.emit('get_genvid_history', {
            limit: options.limit || 20,
            offset: options.offset || 0,
            ...(options.status && { status: options.status })
        });
    }

    getGenvidStatus(queueId) {
        if (!this.state.socket) {
            this.state.error = 'Socket not connected';
            throw new Error('Socket not connected');
        }

        this.state.socket.emit('get_genvid_status', { queueId });
    }

    getGenvidQueue(queueId) {
        return this.state.genvidQueues[queueId] || null;
    }

    disconnect() {
        if (this.state.socket) {
            this.state.socket.disconnect();
            this.state.socket = null;
        }
    }
}

export default new AISocketClient();
